/**
 * Trigger the native Web Share sheet on mobile (WhatsApp, IG DM, FB, Telegram,
 * Messages…). Returns a result describing what happened, so the caller can
 * decide whether to also open a fallback social popup on desktop.
 *
 * Returns:
 *   { used: true,  mode: "share" }       — native picker fired (mobile)
 *   { used: true,  mode: "cancelled" }   — user cancelled native picker
 *   { used: false, mode: "unsupported" } — caller should show fallback UI
 */
export async function tryNativeShare({ title, text, url }) {
    const payload = { title, text, url };
    if (typeof navigator !== "undefined" && navigator.share) {
        try {
            await navigator.share(payload);
            return { used: true, mode: "share" };
        } catch (e) {
            if (e && (e.name === "AbortError" || /cancel/i.test(e.message || ""))) {
                return { used: true, mode: "cancelled" };
            }
            // Other errors → fall through to fallback
        }
    }
    return { used: false, mode: "unsupported" };
}

/** Copy a text+url blob to the clipboard, returning success boolean. */
export async function copyShareBlob(text, url) {
    const blob = [text, url].filter(Boolean).join("\n\n");
    try {
        await navigator.clipboard.writeText(blob);
        return true;
    } catch (_e) {
        try {
            window.prompt("Copia il testo:", blob);
            return true;
        } catch (_e2) {
            return false;
        }
    }
}

/** Build deep-link URLs for one-click sharing on desktop. */
export function buildSocialLinks({ text, url }) {
    const combined = `${text}\n${url}`;
    return {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(combined)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        twitter:  `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
}

/** Build a one-line condition entry: "🟢 SENTINEL · Asciutto ★★★★☆ — Marco, oggi" */
export function formatConditionLine({ name, conditionLabel, rating, userName, when, dot = "•" }) {
    const stars = "★".repeat(rating) + "☆".repeat(Math.max(0, 5 - rating));
    const tail = [userName, when].filter(Boolean).join(", ");
    const tailPart = tail ? ` — ${tail}` : "";
    return `${dot} ${name.toUpperCase()} · ${conditionLabel} ${stars}${tailPart}`;
}

/** Compact relative time formatter for share messages (lang-aware). */
export function shortAgo(iso, lang = "it") {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    const hr = Math.floor(diff / 3600);
    const day = Math.floor(diff / 86400);
    if (day >= 7) return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short" });
    if (day >= 2) return `${day}${lang === "it" ? "gg fa" : "d ago"}`;
    if (day === 1) return lang === "it" ? "ieri" : "yesterday";
    if (hr >= 1) return `${hr}h ${lang === "it" ? "fa" : "ago"}`;
    return lang === "it" ? "oggi" : "today";
}
